import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  Content,
  EmptyState,
  ErrorPanel,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import RefreshIcon from '@material-ui/icons/Refresh';
import { mergeOpsApiRef, GroupPrs, ComponentPrs } from '../../api/MergeOpsApi';
import {
  BuildState,
  BuildStatus,
  DeployState,
  DeployStatus,
  PrecedingBuild,
  PrSummary,
} from '../../api/types';
import { ComponentMergeOpsSection } from './ComponentMergeOpsSection';
import { E2eResultsPanel, useGroupE2eResults } from './E2eResultsPanel';
import { MergeOpsFilterBar } from './MergeOpsFilterBar';
import {
  ALL_DEPLOY_GROUPS,
  distinctDeployGroups,
  EMPTY_FILTER,
  GroupFilter,
  isTerraform,
  matchesCategory,
  matchesDeployGroup,
  matchesMaintenance,
  normalizeCategory,
  ViewPattern,
} from './filter';
import { actionErrorMessage, mergeBlockReason } from '../../hooks/usePrActions';
import { useOwnsEntity } from '../../hooks/useOwnsEntity';
import {
  cloudBuildUrl,
  cloudDeployUrl,
  deployBuildUrl,
  terraformApplyBuildUrl,
} from '../../links';
import {
  buildDeployRunWaves,
  canResumeDeployRun,
  DeployRun,
  DeployRunEnv,
  DeploySkip,
  deployDotKind,
  deployStateLabel,
  deployStatusKey,
  envsForTarget,
  isDeploySkipped,
  formatClockTime,
  formatDuration,
  inFlightWaveEntries,
  isTerminalDeploy,
  mergeWavePrs,
  minIso,
  resumedDeployRun,
  rowElapsedMs,
  unresolvedWaveEntries,
  WaveMergeResult,
  waveOutcome,
  waveProbes,
} from './deployWave';
import {
  approveAllDisabledReason,
  mergeCheckedDisabledReason,
  deployWavesDisabledReason,
} from './bulkActionReasons';
import {
  MergeOutcome,
  componentsToTrack,
  mergeSummaryMessage,
} from './mergeOutcome';
import { mergeOpsTokens } from '../../theme/tokens';

function removePr(
  data: GroupPrs | undefined,
  componentName: string,
  prNumber: number,
): GroupPrs | undefined {
  if (!data) return data;
  return {
    ...data,
    components: data.components.map(c =>
      c.componentName !== componentName
        ? c
        : { ...c, items: c.items.filter(pr => pr.number !== prNumber) },
    ),
  };
}

function updatePr(
  data: GroupPrs | undefined,
  componentName: string,
  prNumber: number,
  updater: (pr: PrSummary) => PrSummary,
): GroupPrs | undefined {
  if (!data) return data;
  return {
    ...data,
    components: data.components.map(component =>
      component.componentName !== componentName
        ? component
        : {
            ...component,
            items: component.items.map(pr =>
              pr.number === prNumber ? updater(pr) : pr,
            ),
          },
    ),
  };
}

const useStyles = makeStyles(theme => {
  const t = mergeOpsTokens(theme);
  return {
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1.5),
    },
    summary: {
      marginBottom: theme.spacing(2),
      color: theme.palette.text.secondary,
    },
    flowPanel: {
      backgroundColor: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: t.radius,
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    flowRail: {
      display: 'grid',
      gridTemplateColumns:
        'minmax(160px, 1fr) 40px minmax(160px, 1fr) 40px minmax(160px, 1fr)',
      alignItems: 'stretch',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
      },
    },
    flowRailCombined: {
      gridTemplateColumns: 'minmax(160px, 0.8fr) 40px minmax(260px, 1.2fr)',
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
      },
    },
    flowNode: {
      width: '100%',
      minHeight: 116,
      border: `1px solid ${t.border}`,
      borderRadius: t.radius,
      display: 'block',
      textAlign: 'left',
      padding: theme.spacing(1.5),
      backgroundColor: t.elevated,
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden',
      transition: theme.transitions.create(
        ['background-color', 'border-color', 'box-shadow'],
        { duration: theme.transitions.duration.shortest },
      ),
      '&:hover': {
        borderColor: t.accent,
        backgroundColor: t.hover,
      },
      '&:focus-visible': {
        outline: `2px solid ${t.accent}`,
        outlineOffset: 2,
      },
    },
    flowNodeActive: {
      borderColor: t.accent,
      backgroundColor: t.selected,
      boxShadow: t.shadow,
      '&:before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 3,
        backgroundColor: t.accent,
      },
    },
    flowNodeHeader: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1.25),
    },
    flowNodeTitle: {
      fontWeight: 700,
      letterSpacing: 0,
    },
    flowNodeMeta: {
      color: theme.palette.text.secondary,
      fontSize: '0.78rem',
      lineHeight: 1.4,
    },
    flowNodeBadges: {
      display: 'flex',
      gap: theme.spacing(0.75),
      flexWrap: 'wrap',
      marginTop: theme.spacing(1),
    },
    phaseBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: theme.spacing(0.5),
      height: 24,
      padding: theme.spacing(0, 0.85),
      borderRadius: 999,
      fontSize: '0.72rem',
      fontWeight: 700,
      border: `1px solid ${t.badgeBorder}`,
      color: theme.palette.text.secondary,
      backgroundColor: t.badgeBg,
      // Keep the badge intact when the card is narrow: never break "1 blocked"
      // across lines, and don't let flex squeeze it below its content width.
      whiteSpace: 'nowrap',
      flexShrink: 0,
    },
    // Small leading dot so phase status is scannable at a glance, not just by
    // colour (colour-blind safe + reads in both themes).
    phaseBadgeDot: {
      width: 7,
      height: 7,
      borderRadius: '50%',
      backgroundColor: 'currentColor',
      flexShrink: 0,
    },
    phaseStatusReady: {
      color: t.state.ready.fg,
      borderColor: t.state.ready.border,
      backgroundColor: t.state.ready.bg,
    },
    phaseStatusBlocked: {
      color: t.state.blocked.fg,
      borderColor: t.state.blocked.border,
      backgroundColor: t.state.blocked.bg,
    },
    // Connector between phase cards: a hairline rail with a chevron, replacing
    // the literal "->" text. Subtle by default, tinted teal once a downstream
    // phase is reachable.
    flowArrow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      alignSelf: 'center',
      color: t.isDark ? 'rgba(198, 198, 204, 0.55)' : 'rgba(71, 85, 105, 0.5)',
      [theme.breakpoints.down('sm')]: {
        transform: 'rotate(90deg)',
        padding: theme.spacing(0.5, 0),
      },
    },
    flowArrowLine: {
      flex: 1,
      height: 1,
      backgroundColor: t.border,
    },
    flowArrowChevron: {
      fontSize: '1.1rem',
      lineHeight: 1,
      margin: theme.spacing(0, 0.25),
    },
    runTitle: {
      fontWeight: 700,
      marginBottom: theme.spacing(0.5),
    },
    runCopy: {
      color: theme.palette.text.secondary,
      fontSize: '0.85rem',
    },
    progressPanel: {
      backgroundColor: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: t.radius,
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(2),
    },
    progressHeader: {
      display: 'flex',
      alignItems: 'baseline',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(0.75),
    },
    progressTitle: {
      fontWeight: 700,
    },
    progressDismiss: {
      marginLeft: 'auto',
      fontWeight: 700,
      color: theme.palette.text.secondary,
    },
    // The recovery action on a stopped run's rows: caution-toned so it reads
    // as "deliberate escape hatch" and stands out from the muted row text,
    // without competing with the primary actions.
    skipButton: {
      fontWeight: 700,
      color: t.state.caution.fg,
      backgroundColor: t.state.caution.bg,
      border: `1px solid ${t.state.caution.border}`,
      '&:hover': {
        backgroundColor: t.state.caution.bg,
        borderColor: t.state.caution.fg,
      },
    },
    progressRows: {
      display: 'grid',
    },
    progressPhaseGroup: {
      display: 'grid',
      gridTemplateColumns: '136px 1fr',
      gap: theme.spacing(1.25),
      padding: theme.spacing(0.75, 0),
      borderTop: `1px solid ${theme.palette.divider}`,
      '&:first-child': {
        borderTop: 0,
        paddingTop: 0,
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
      },
    },
    progressPhaseRail: {
      color: theme.palette.text.secondary,
      fontSize: '0.78rem',
      fontWeight: 700,
      paddingTop: theme.spacing(0.5),
    },
    // 失敗の強調。バッジのドットだけだと、どの段のどの行で止まったかが
    // 流し見で読み取れない (色の直書きはせず t.state.blocked を使う)
    progressPanelFailed: {
      borderColor: t.state.blocked.border,
    },
    progressPhaseGroupFailed: {
      borderLeft: `2px solid ${t.state.blocked.fg}`,
      paddingLeft: theme.spacing(1),
    },
    progressRowFailed: {
      backgroundColor: t.state.blocked.bg,
      borderRadius: t.radius,
      paddingLeft: theme.spacing(0.75),
      paddingRight: theme.spacing(0.75),
      marginLeft: theme.spacing(-0.75),
      marginRight: theme.spacing(-0.75),
    },
    progressRow: {
      display: 'grid',
      gridTemplateColumns: 'minmax(180px, 0.8fr) minmax(220px, 1.2fr) 150px',
      gap: theme.spacing(1),
      alignItems: 'center',
      padding: theme.spacing(0.5, 0),
      borderTop: `1px solid ${theme.palette.divider}`,
      '&:first-child': {
        borderTop: 0,
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        gap: theme.spacing(0.25),
      },
    },
    progressProduct: {
      fontWeight: 600,
    },
    progressPr: {
      color: theme.palette.text.secondary,
      fontSize: '0.82rem',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    progressStatus: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: theme.spacing(0.75),
      color: theme.palette.text.secondary,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'flex-start',
      },
    },
    progressElapsed: {
      fontFamily: 'monospace',
      fontVariantNumeric: 'tabular-nums',
      textTransform: 'none',
      color: theme.palette.text.hint,
    },
    statusDot: {
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: theme.palette.text.disabled,
    },
    runningDot: {
      backgroundColor: theme.palette.primary.main,
    },
    completedDot: {
      backgroundColor: theme.palette.success.main,
    },
    failedDot: {
      backgroundColor: theme.palette.error.main,
    },
    detailsLabel: {
      color: theme.palette.text.secondary,
      fontSize: '0.78rem',
      fontWeight: 700,
      textTransform: 'uppercase',
    },
    detailsHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1.5),
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1.5),
      padding: theme.spacing(1.25, 1.5),
      position: 'sticky',
      top: 0,
      zIndex: 2,
      backgroundColor: t.surfaceGlass,
      border: `1px solid ${t.border}`,
      borderRadius: t.radius,
      backdropFilter: 'blur(10px)',
      flexWrap: 'wrap',
    },
    detailsTitleGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      minWidth: 0,
    },
    detailsActions: {
      display: 'flex',
      gap: theme.spacing(1.25),
      justifyContent: 'end',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
    },
    actionGroup: {
      display: 'grid',
      gap: theme.spacing(0.75),
      padding: theme.spacing(0.85),
      borderLeft: `1px solid ${t.badgeBorder}`,
      '&:first-child': {
        borderLeft: 0,
      },
      [theme.breakpoints.down('sm')]: {
        borderLeft: 0,
        borderTop: `1px solid ${theme.palette.divider}`,
        width: '100%',
      },
    },
    actionGroupLabel: {
      color: theme.palette.text.secondary,
      fontSize: '0.68rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: 0.4,
    },
    actionGroupButtons: {
      display: 'flex',
      gap: theme.spacing(0.75),
      flexWrap: 'wrap',
      [theme.breakpoints.down('xs')]: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        width: '100%',
      },
    },
    detailsActionButton: {
      minWidth: 128,
      height: 36,
      borderRadius: 6,
      fontWeight: 700,
    },
    mergeActionButton: {
      '&.Mui-disabled': {
        border: `1px solid ${t.badgeBorder}`,
        backgroundColor: t.isDark
          ? 'rgba(196, 196, 204, 0.1)'
          : 'rgba(15, 23, 42, 0.05)',
      },
    },
    idleSummary: {
      borderTop: `1px solid ${theme.palette.divider}`,
      paddingTop: theme.spacing(1),
      marginTop: theme.spacing(1.5),
    },
    idleTitle: {
      color: theme.palette.text.secondary,
      fontSize: '0.78rem',
      fontWeight: 700,
      textTransform: 'uppercase',
      marginBottom: theme.spacing(0.5),
    },
    section: {
      marginBottom: theme.spacing(1.25),
    },
    deployPhaseGroup: {
      display: 'grid',
      gridTemplateColumns: '136px 1fr',
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.25, 0),
      borderTop: `1px solid ${t.borderSoft}`,
      '&:first-child': {
        borderTop: 0,
        paddingTop: 0,
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
      },
    },
    deployPhaseRail: {
      display: 'grid',
      gap: theme.spacing(0.75),
      justifyItems: 'start',
      color: theme.palette.text.secondary,
      fontSize: '0.78rem',
      fontWeight: 700,
      paddingTop: theme.spacing(0.75),
      position: 'relative',
      '&:after': {
        content: '""',
        position: 'absolute',
        top: theme.spacing(3),
        bottom: theme.spacing(0.5),
        left: 7,
        width: 1,
        backgroundColor: t.badgeBorder,
      },
    },
    deployPhaseIndex: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 20,
      height: 20,
      borderRadius: '50%',
      color: t.isDark ? theme.palette.common.black : theme.palette.common.white,
      backgroundColor: t.accent,
      marginRight: theme.spacing(0.5),
      fontSize: '0.72rem',
    },
    deployPhaseItems: {
      display: 'grid',
      gap: theme.spacing(1),
    },
    phase: {
      marginBottom: theme.spacing(2.5),
    },
    phaseHeader: {
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
      flexWrap: 'wrap',
    },
    phaseHeaderText: {
      display: 'flex',
      alignItems: 'baseline',
      gap: theme.spacing(1),
      flexWrap: 'wrap',
    },
    phaseTitle: {
      fontWeight: 700,
      letterSpacing: 0,
    },
    phaseMeta: {
      color: theme.palette.text.secondary,
      fontSize: '0.8rem',
    },
    bulkStatus: {
      marginBottom: theme.spacing(1.5),
    },
    previewStep: {
      padding: theme.spacing(1.25, 0),
    },
    previewAlert: {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1.5),
    },
    previewPhaseCard: {
      padding: theme.spacing(1.25, 0),
    },
    previewPhaseHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    previewPhaseTitle: {
      fontWeight: 700,
    },
    previewProductGrid: {
      display: 'grid',
      gap: 0,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 6,
      overflow: 'hidden',
    },
    previewProductName: {
      fontWeight: 700,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    previewPrRow: {
      display: 'grid',
      gridTemplateColumns:
        'minmax(180px, 0.8fr) 76px minmax(220px, 1.2fr) 92px',
      gap: theme.spacing(1),
      alignItems: 'center',
      padding: theme.spacing(0.75, 1),
      borderTop: `1px solid ${theme.palette.divider}`,
      '&:first-child': {
        borderTop: 0,
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr',
        gap: theme.spacing(0.25),
      },
    },
    previewPrNumber: {
      fontWeight: 700,
    },
    previewPrTitle: {
      color: theme.palette.text.secondary,
      fontSize: '0.86rem',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    previewPrTarget: {
      color: theme.palette.text.secondary,
      fontSize: '0.75rem',
      fontWeight: 700,
      textTransform: 'uppercase',
    },
    previewNoPr: {
      color: theme.palette.text.hint,
      fontSize: '0.84rem',
      gridColumn: '2 / span 3',
    },
    simulationOverview: {
      display: 'flex',
      gap: theme.spacing(1),
      flexWrap: 'wrap',
      marginTop: theme.spacing(1.5),
      marginBottom: theme.spacing(1),
    },
    previewStepTitle: {
      fontWeight: 700,
      marginBottom: theme.spacing(0.5),
    },
    previewWait: {
      color: theme.palette.text.hint,
      fontSize: '0.8rem',
      marginTop: theme.spacing(0.75),
    },
    idle: {
      marginTop: theme.spacing(2),
      color: theme.palette.text.hint,
      fontSize: '0.8rem',
    },
  };
});

const RenderError = ({ error }: { error: Error }) => {
  if ((error as any).cause?.response || (error as any).response) {
    return <ResponseErrorPanel error={error as any} />;
  }
  return <ErrorPanel error={error} />;
};

interface WaveSection {
  key: string;
  title: string;
  kind: 'build' | 'deploy';
  /** Deploy wave number (ascending order); undefined for the build section. */
  wave?: number;
  components: ComponentPrs[];
}

type OperationMode = 'build' | 'deploy';
type ExecutionMode = 'dry-run' | 'production';
type SimStatus = 'queued' | 'running' | 'completed';
type DeployTarget = 'stg' | 'prd' | 'all';
type FlowStep = 'build' | DeployTarget;

interface PreviewRun {
  operation: OperationMode;
  executionMode: ExecutionMode;
}

interface ActiveRun {
  mode: OperationMode;
  executionMode: ExecutionMode;
  buildComponents: ComponentPrs[];
  deploySections: WaveSection[];
}

interface PrTarget {
  componentName: string;
  pr: PrSummary;
}

interface BulkStatus {
  severity: 'info' | 'success' | 'error';
  message: string;
}

// Poll fast at first (deploys/builds usually finish in the first minutes) then
// stretch the interval to cut API calls on long-running runs. Delay is chosen
// from elapsed run/wave time on each tick.
const POLL_MIN_MS = 12_000;
const POLL_MID_MS = 20_000;
const POLL_MAX_MS = 30_000;
const POLL_RAMP_MS = 2 * 60 * 1000; // < 2min: fast; < 4min: mid; else: slow.
function nextPollDelay(elapsedMs: number): number {
  if (elapsedMs < POLL_RAMP_MS) return POLL_MIN_MS;
  if (elapsedMs < POLL_RAMP_MS * 2) return POLL_MID_MS;
  return POLL_MAX_MS;
}

// `since` lower-bounds which Cloud Builds count as "this run" (create_time >
// since). It MUST be captured BEFORE merging: the merge immediately triggers the
// run_kit/deploy_kit build, so capturing after the merge resolves can place
// `since` past the build's create_time, which then excludes it forever (the
// progress panel stays "waiting for deploy" even though the build ran). We also
// back-date a small margin to absorb browser/GCP clock skew. Re-deploying the
// same component within this window is effectively impossible, so it won't match
// a stale build.
const SINCE_SKEW_MS = 60_000;
function sinceForMerge(): string {
  return new Date(Date.now() - SINCE_SKEW_MS).toISOString();
}

// `since` from the server-reported merge time (ApproveOrMergeResult.mergedAt):
// no client clock skew to absorb, so only a 1s backdate for the strict
// create_time > since filter's second-granularity edge. Null for a missing or
// unparseable value — the caller falls back to the browser-clock sinceForMerge.
function sinceFromMergedAt(mergedAtIso: string | undefined): string | null {
  if (!mergedAtIso) return null;
  const ms = Date.parse(mergedAtIso);
  if (Number.isNaN(ms)) return null;
  return new Date(ms - 1000).toISOString();
}

// Real Cloud Build progress tracking after a build (tag) Merge Checked.
const BUILD_TIMEOUT_MS = 60 * 60 * 1000; // 1h, per product requirement
const TERMINAL_BUILD_STATES = new Set<BuildState>([
  'success',
  'failure',
  'timeout',
  'cancelled',
]);

interface BuildRun {
  since: string; // ISO merge time; only builds at/after this count
  startedMs: number; // wall-clock start, for the 1h timeout
  components: string[]; // component names being built
}

// Persist the in-flight build run so a page reload keeps showing progress
// (the build keeps running on Cloud Build regardless of the tab).
const BUILD_RUN_STORAGE_PREFIX = 'mergeops.buildRun.';

interface PersistedBuildRun {
  run: BuildRun;
  statuses: Record<string, BuildStatus>;
  timedOut: boolean;
}

function buildRunStorageKey(groupName: string): string {
  return `${BUILD_RUN_STORAGE_PREFIX}${groupName}`;
}

// Per-group, per-field key for persisted view selections (flow step / view
// pattern / deploy target / category).
const VIEW_STATE_STORAGE_PREFIX = 'mergeops.view.';
function viewStateKey(groupName: string, field: string): string {
  return `${VIEW_STATE_STORAGE_PREFIX}${groupName}.${field}`;
}

// useState that mirrors to localStorage, so a view selection survives
// navigating away from the group page and reloads. Keyed per group + field.
function usePersistedState<T>(
  key: string,
  initial: T,
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw !== null ? (JSON.parse(raw) as T) : initial;
    } catch {
      return initial;
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // localStorage unavailable (private mode / quota) — selection just won't
      // persist; in-memory state still works.
    }
  }, [key, value]);
  return [value, setValue];
}

function loadPersistedBuildRun(groupName: string): PersistedBuildRun | null {
  try {
    const raw = window.localStorage.getItem(buildRunStorageKey(groupName));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedBuildRun;
    if (
      !parsed?.run ||
      !Array.isArray(parsed.run.components) ||
      typeof parsed.run.startedMs !== 'number'
    ) {
      return null;
    }
    // Drop runs past the 1h window so stale panels don't reappear days later.
    if (Date.now() - parsed.run.startedMs >= BUILD_TIMEOUT_MS) {
      window.localStorage.removeItem(buildRunStorageKey(groupName));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function isTerminalBuild(state: BuildState): boolean {
  return TERMINAL_BUILD_STATES.has(state);
}

/** Status-dot category for a build state (avoids nested ternaries at call site). */
function buildDotKind(
  state: BuildState,
): 'running' | 'done' | 'failed' | 'idle' {
  if (state === 'working') return 'running';
  if (state === 'success') return 'done';
  if (state === 'failure' || state === 'timeout' || state === 'cancelled') {
    return 'failed';
  }
  return 'idle';
}

/**
 * Why a row can sit on "waiting for build" with no build ever arriving: the
 * component was built just before this run started — its build PR having been
 * merged by an earlier run — so its build predates the run's `since` and is
 * never matched. Without this the panel just looks stuck.
 */
function precedingBuildNote(preceding: PrecedingBuild): string {
  const ranAt = preceding.finishTime ?? preceding.startTime;
  const time = ranAt ? new Date(ranAt).toLocaleTimeString() : null;
  return `built before this run: ${buildStateLabel(preceding.state)}${
    time ? ` at ${time}` : ''
  }`;
}

function buildStateLabel(state: BuildState): string {
  switch (state) {
    case 'none':
      return 'waiting for build';
    case 'queued':
      return 'queued';
    case 'working':
      return 'building';
    case 'success':
      return 'success';
    case 'failure':
      return 'failed';
    case 'timeout':
      return 'timed out';
    case 'cancelled':
      return 'cancelled';
    default:
      return 'unknown';
  }
}

// Real Cloud Run deploy progress, polled wave-by-wave after merging deploy PRs.
const DEPLOY_TIMEOUT_MS = 60 * 60 * 1000; // 1h per wave, mirroring builds.
// A stopped run keeps OBSERVING the wave's already-triggered builds (no
// merging, no advancing): stopping the gate must not freeze the panel while
// sibling deploys are still running in Cloud Build. Slow cadence — nothing
// is gated on it — and a hard cap so an abandoned failed panel stops calling
// the API eventually.
const OBSERVE_POLL_MS = 30_000;
const OBSERVE_MAX_MS = 15 * 60 * 1000;
const DEPLOY_RUN_STORAGE_PREFIX = 'mergeops.deployRun.';

interface PersistedDeployRun {
  run: DeployRun;
  statuses: Record<string, DeployStatus>;
  timedOut: boolean;
}

function deployRunStorageKey(groupName: string): string {
  return `${DEPLOY_RUN_STORAGE_PREFIX}${groupName}`;
}

function loadPersistedDeployRun(groupName: string): PersistedDeployRun | null {
  try {
    const raw = window.localStorage.getItem(deployRunStorageKey(groupName));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedDeployRun;
    if (!parsed?.run || !Array.isArray(parsed.run.waves)) return null;
    // Drop runs whose current wave is past its timeout window so a stale panel
    // doesn't reappear days later (terminal runs are kept until dismissed).
    const active =
      parsed.run.phase === 'merging' || parsed.run.phase === 'polling';
    if (active && Date.now() - parsed.run.waveStartedMs >= DEPLOY_TIMEOUT_MS) {
      window.localStorage.removeItem(deployRunStorageKey(groupName));
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function sortComponents(components: ComponentPrs[]): ComponentPrs[] {
  return [...components].sort((a, b) =>
    a.componentName.localeCompare(b.componentName),
  );
}

function withItems(component: ComponentPrs, items: ComponentPrs['items']) {
  return { ...component, items };
}

function prKey(componentName: string, prNumber: number): string {
  return `${componentName}:${prNumber}`;
}

function targetsFromComponents(components: ComponentPrs[]): PrTarget[] {
  return components.flatMap(component =>
    component.items.map(pr => ({
      componentName: component.componentName,
      pr,
    })),
  );
}

function targetsFromSections(sections: WaveSection[]): PrTarget[] {
  return sections.flatMap(section => targetsFromComponents(section.components));
}

function canMergeTargets(targets: PrTarget[]): boolean {
  return (
    targets.length > 0 && targets.every(target => !mergeBlockReason(target.pr))
  );
}

function blockedTargetCount(targets: PrTarget[]): number {
  return targets.filter(target => mergeBlockReason(target.pr)).length;
}

function deployTargetLabel(target: DeployTarget): string {
  if (target === 'all') return 'to stg/prd';
  return `to ${target}`;
}

function deployTargetEnvs(pr: PrSummary): Array<'stg' | 'prd'> {
  if (pr.env === 'stg' || pr.env === 'prd') return [pr.env];
  if (pr.env === 'all') return ['stg', 'prd'];
  return [];
}

function previewTargetLabel(pr: PrSummary): string {
  if (pr.prType === 'tag') return 'build';
  if (pr.env === 'all') return 'stg/prd';
  return pr.env ?? 'deploy';
}

function matchesDeployTarget(pr: PrSummary, target: DeployTarget): boolean {
  return pr.prType === 'deploy' && pr.env === target;
}

// Group deploy components into ArgoCD-style waves: each component carries its
// own integer `wave`, and waves render in ascending order. `includeEmpty` keeps
// components with no matching deploy PR (used by the preview to show coverage).
function deploySectionsByWave(
  components: ComponentPrs[],
  deployTarget: DeployTarget,
  keyPrefix: string,
  includeEmpty: boolean,
): WaveSection[] {
  const byWave = new Map<number, ComponentPrs[]>();
  for (const component of components) {
    const deployItems = component.items.filter(pr =>
      matchesDeployTarget(pr, deployTarget),
    );
    if (!includeEmpty && deployItems.length === 0) continue;
    const list = byWave.get(component.wave) ?? [];
    list.push(withItems(component, deployItems));
    byWave.set(component.wave, list);
  }
  return Array.from(byWave.keys())
    .sort((a, b) => a - b)
    .map(wave => ({
      key: `${keyPrefix}:wave:${wave}`,
      title: `Wave ${wave}`,
      kind: 'deploy' as const,
      wave,
      components: sortComponents(byWave.get(wave)!),
    }));
}

function buildWaveSections(
  components: ComponentPrs[],
  deployTarget: DeployTarget,
): WaveSection[] {
  const sections: WaveSection[] = [];

  const buildComponents = sortComponents(
    components
      .map(c =>
        withItems(
          c,
          c.items.filter(pr => pr.prType === 'tag'),
        ),
      )
      .filter(c => c.items.length > 0),
  );
  if (buildComponents.length > 0) {
    sections.push({
      key: 'build',
      title: 'Build',
      kind: 'build',
      components: buildComponents,
    });
  }

  sections.push(
    ...deploySectionsByWave(components, deployTarget, 'deploy', false),
  );
  return sections;
}

function buildPreviewDeploySections(
  components: ComponentPrs[],
  deployTarget: DeployTarget,
): WaveSection[] {
  return deploySectionsByWave(components, deployTarget, 'deploy-preview', true);
}

function countPrs(components: ComponentPrs[]): number {
  return components.reduce((sum, c) => sum + c.items.length, 0);
}

function formatCount(count: number, singular: string, plural?: string): string {
  return `${count} ${count === 1 ? singular : plural ?? `${singular}s`}`;
}

function formatBuildPrs(count: number): string {
  return count === 1 ? '1 PR to build' : `${count} PRs to build`;
}

function formatDeployPrs(count: number): string {
  return count === 1 ? '1 PR to deploy' : `${count} PRs to deploy`;
}

function executionLabel(mode: ExecutionMode): string {
  return mode === 'production' ? 'Production' : 'Dry run';
}

function reviewTitle(run: PreviewRun | null): string {
  if (!run) return 'Review operation';
  const operation = run.operation === 'build' ? 'build' : 'deploy';
  return `Review ${executionLabel(
    run.executionMode,
  ).toLowerCase()} ${operation}`;
}

function startButtonLabel(run: PreviewRun | null): string {
  if (!run) return 'Start';
  const operation = run.operation === 'build' ? 'build' : 'deploy';
  if (run.executionMode === 'dry-run') {
    return `Start ${operation} dry run`;
  }
  return `Start production ${operation}`;
}

function statusLabel(status: SimStatus): string {
  if (status === 'running') return 'running';
  if (status === 'completed') return 'completed';
  return 'queued';
}

function simulationStatus(frame: number, completeAt: number): SimStatus {
  if (frame <= 0) return 'queued';
  if (frame >= completeAt) return 'completed';
  return 'running';
}

const Header = ({
  groupName,
  onRefresh,
}: {
  groupName: string;
  onRefresh: () => void;
}) => {
  const classes = useStyles();
  return (
    <Box className={classes.header}>
      <Typography variant="h5">MergeOps — {groupName}</Typography>
      <Tooltip title="Refresh">
        <IconButton onClick={onRefresh} aria-label="Refresh">
          <RefreshIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const ProductPreviewList = ({ components }: { components: ComponentPrs[] }) => {
  const classes = useStyles();
  return (
    <Box className={classes.previewProductGrid}>
      {components.flatMap(component => {
        if (component.items.length === 0) {
          return [
            <Box key={component.projectName} className={classes.previewPrRow}>
              <Typography className={classes.previewProductName}>
                {component.projectName}
              </Typography>
              <Typography className={classes.previewNoPr}>
                No matching PR
              </Typography>
            </Box>,
          ];
        }
        return component.items.map(pr => (
          <Box
            key={`${component.projectName}:${pr.number}`}
            className={classes.previewPrRow}
          >
            <Typography className={classes.previewProductName}>
              {component.projectName}
            </Typography>
            <Link
              href={pr.url}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.previewPrNumber}
            >
              #{pr.number}
            </Link>
            <Typography className={classes.previewPrTitle}>
              {pr.title}
            </Typography>
            <span className={classes.previewPrTarget}>
              {previewTargetLabel(pr)}
            </span>
          </Box>
        ));
      })}
    </Box>
  );
};

const FlowNode = ({
  title,
  caption,
  prCount,
  waveCount,
  blockedCount,
  selected,
  onClick,
}: {
  title: string;
  caption: string;
  prCount: number;
  waveCount?: number;
  blockedCount: number;
  selected: boolean;
  onClick: () => void;
}) => {
  const classes = useStyles();
  let statusText = 'Ready';
  if (prCount === 0) {
    statusText = 'No PR';
  } else if (blockedCount > 0) {
    statusText = `${blockedCount} blocked`;
  }
  return (
    <ButtonBase
      className={`${classes.flowNode} ${
        selected ? classes.flowNodeActive : ''
      }`}
      onClick={onClick}
      aria-pressed={selected}
    >
      <Box className={classes.flowNodeHeader}>
        <Box>
          <Typography className={classes.flowNodeTitle}>{title}</Typography>
          <Typography className={classes.flowNodeMeta}>{caption}</Typography>
        </Box>
        <span
          className={[
            classes.phaseBadge,
            blockedCount > 0 ? classes.phaseStatusBlocked : '',
            prCount > 0 && blockedCount === 0 ? classes.phaseStatusReady : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {prCount > 0 && <span className={classes.phaseBadgeDot} />}
          {statusText}
        </span>
      </Box>
      <Box className={classes.flowNodeBadges}>
        <span className={classes.phaseBadge}>{formatCount(prCount, 'PR')}</span>
        {waveCount !== undefined && (
          <span className={classes.phaseBadge}>
            {formatCount(waveCount, 'wave')}
          </span>
        )}
      </Box>
    </ButtonBase>
  );
};

const FlowArrow = () => {
  const classes = useStyles();
  return (
    <Box className={classes.flowArrow} aria-hidden>
      <span className={classes.flowArrowLine} />
      <span className={classes.flowArrowChevron}>›</span>
      <span className={classes.flowArrowLine} />
    </Box>
  );
};

/**
 * Clock time the Cloud Build was triggered at, shown after the elapsed counter
 * (`00:03:12 · 14:32:05〜`). Renders nothing until a build has been matched.
 */
const TriggeredAt = ({ createTime }: { createTime?: string | null }) => {
  if (!createTime) return null;
  const triggeredAt = formatClockTime(createTime);
  if (!triggeredAt) return null;
  return (
    <span
      title={`Cloud Build triggered at ${new Date(
        createTime,
      ).toLocaleString()}`}
    >
      {` · ${triggeredAt}〜`}
    </span>
  );
};

// Remount the content whenever the group changes: the inner component holds
// per-group state (usePersistedState initializers run once, and the build/
// deploy-run localStorage mirror effects write under the CURRENT group's key) —
// navigating group A → B without a remount would carry A's state over and
// mirror it into B's storage keys (even deleting B's persisted in-flight run).
export const EntityGroupMergeOpsContent = () => {
  const { entity } = useEntity();
  return <GroupMergeOpsContent key={entity.metadata.name} />;
};

function GroupMergeOpsContent() {
  const classes = useStyles();
  const { entity } = useEntity();
  const api = useApi(mergeOpsApiRef);
  const groupName = entity.metadata.name;
  // Only members of this group (or a descendant) may operate, matching the
  // backend's assertCanOperate. For other teams' groups every action is
  // disabled (the backend would 403 anyway).
  const ownsGroup = useOwnsEntity(stringifyEntityRef(entity));
  const [refreshTick, setRefreshTick] = useState(0);
  // View selections persisted per group (survive navigation away + reload).
  const [persistedFilter, setPersistedFilter] = usePersistedState<GroupFilter>(
    viewStateKey(groupName, 'filter'),
    EMPTY_FILTER,
  );
  // The maintenance toggle is deliberately session-only (not persisted):
  // every visit starts with maintenance-mode components shown and deployable;
  // unticking narrows the current session only.
  const [includeMaintenance, setIncludeMaintenance] = useState(true);
  const filter: GroupFilter = {
    ...persistedFilter,
    // Clamp what came out of storage: older versions persisted
    // category: 'application' (now 'cloudrun').
    category: normalizeCategory(persistedFilter.category),
    includeMaintenance,
  };
  const setFilter = (next: GroupFilter) => {
    setIncludeMaintenance(next.includeMaintenance !== false);
    setPersistedFilter({ category: next.category });
  };
  // Local copy of the fetched data so Approve/Merge can update a single PR
  // optimistically without refetching (and flashing) the whole group.
  const [data, setData] = useState<GroupPrs | undefined>(undefined);
  const [previewRun, setPreviewRun] = useState<PreviewRun | null>(null);
  const [activeRun, setActiveRun] = useState<ActiveRun | null>(null);
  const [simulationFrame, setSimulationFrame] = useState(0);
  const [deployTarget, setDeployTarget] = usePersistedState<DeployTarget>(
    viewStateKey(groupName, 'deployTarget'),
    'stg',
  );
  const [selectedFlowStep, setSelectedFlowStep] = usePersistedState<FlowStep>(
    viewStateKey(groupName, 'flowStep'),
    'build',
  );
  const [viewPattern, setViewPattern] = usePersistedState<ViewPattern>(
    viewStateKey(groupName, 'viewPattern'),
    'split',
  );
  const [deployGroup, setDeployGroup] = usePersistedState<string>(
    viewStateKey(groupName, 'deployGroup'),
    ALL_DEPLOY_GROUPS,
  );
  // Persisted independently, `flowStep: 'all'` can be restored alongside
  // `viewPattern: 'split'` (where the Stg & Prd node doesn't exist) — clamp to
  // a step the split rail can actually select.
  useEffect(() => {
    if (viewPattern === 'split' && selectedFlowStep === 'all') {
      setSelectedFlowStep(deployTarget !== 'all' ? deployTarget : 'stg');
    }
  }, [viewPattern, selectedFlowStep, deployTarget, setSelectedFlowStep]);
  const [diffExpanded, setDiffExpanded] = useState(true);
  const [componentDiffExpanded, setComponentDiffExpanded] = useState<
    Record<string, boolean>
  >({});
  const [selectedPrKeys, setSelectedPrKeys] = useState<Set<string>>(
    () => new Set(),
  );
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkStatus, setBulkStatus] = useState<BulkStatus | null>(null);
  // Real Cloud Build progress after a build Merge Checked. Restored from
  // localStorage so a reload keeps showing/continuing the run.
  const [buildRun, setBuildRun] = useState<BuildRun | null>(
    () => loadPersistedBuildRun(groupName)?.run ?? null,
  );
  const [buildStatuses, setBuildStatuses] = useState<
    Record<string, BuildStatus>
  >(() => loadPersistedBuildRun(groupName)?.statuses ?? {});
  const [buildTimedOut, setBuildTimedOut] = useState(
    () => loadPersistedBuildRun(groupName)?.timedOut ?? false,
  );
  // Real Cloud Run deploy progress, driven wave-by-wave. Restored from
  // localStorage so a reload keeps watching/continuing the run.
  const [deployRun, setDeployRun] = useState<DeployRun | null>(
    () => loadPersistedDeployRun(groupName)?.run ?? null,
  );
  const [deployStatuses, setDeployStatuses] = useState<
    Record<string, DeployStatus>
  >(() => loadPersistedDeployRun(groupName)?.statuses ?? {});
  const [deployTimedOut, setDeployTimedOut] = useState(
    () => loadPersistedDeployRun(groupName)?.timedOut ?? false,
  );
  // Consecutive status-poll ticks whose fetch failed. The poll deliberately
  // keeps ticking through errors (a blip must not kill the run), but a
  // persistent failure looks exactly like "waiting for deploy": without
  // surfacing it, the wave silently runs out its 1h timeout and reports a
  // false failure. Shown in the progress panels from 2 consecutive failures.
  const [deployPollFailStreak, setDeployPollFailStreak] = useState(0);
  const deployPollFailStreakRef = useRef(0);
  const [buildPollFailStreak, setBuildPollFailStreak] = useState(0);
  // Whether the stopped-run observer below is still watching in-flight builds
  // (keeps the elapsed-time ticker running while it is).
  const [observeActive, setObserveActive] = useState(false);
  // Cancel signal for the active deploy run. The phase flips to 'cancelled'
  // immediately, but an already-running merge/poll loop holds a stale closure
  // over `deployRun` — this ref is how it learns to bail out before merging
  // anything else.
  const deployCancelRequestedRef = useRef(false);
  // Whether the cancel-confirmation dialog is open. Cancel is confirmed first
  // because it can't undo the in-flight wave's already-running deploy builds.
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  // The (component, env) row a Skip was requested for, while its confirmation
  // dialog (with the required reason) is open. Null = dialog closed.
  const [skipTarget, setSkipTarget] = useState<{
    componentName: string;
    projectName: string;
    env: DeployRunEnv;
    state: DeployState;
    wave: number;
  } | null>(null);
  const [skipReason, setSkipReason] = useState('');
  const [skipSubmitting, setSkipSubmitting] = useState(false);
  // Outcome is computed from this ref (always the freshest merged statuses) so
  // the poll loop doesn't depend on the statuses state and restart every tick.
  // Seeded from storage so a reload keeps earlier waves' results in view.
  const deployStatusesRef = useRef<Record<string, DeployStatus>>(
    loadPersistedDeployRun(groupName)?.statuses ?? {},
  );
  // Ticks every second while a deploy run is active so the per-row elapsed time
  // visibly advances (a long "deploying" otherwise looks stuck).
  const [nowMs, setNowMs] = useState(() => Date.now());
  // When the group list was read from GitHub, so every row can show its own
  // status age (and re-stamp it when refreshed individually).
  const [fetchedAtMs, setFetchedAtMs] = useState<number | undefined>(undefined);

  const state = useAsync(
    async (): Promise<GroupPrs> => api.listPrsByGroup({ groupName }),
    [groupName, refreshTick],
  );
  // Fetched here rather than inside the panel so the request goes out with the
  // PR list instead of waiting behind the loading gate below (the PR list takes
  // seconds — one GitHub search per project — and the panel renders after it).
  const e2eResults = useGroupE2eResults(groupName);

  useEffect(() => {
    if (state.value) {
      setData(state.value);
      setFetchedAtMs(Date.now());
    }
  }, [state.value]);

  useEffect(() => {
    if (!activeRun) return undefined;
    setSimulationFrame(0);
    const intervalId = window.setInterval(() => {
      setSimulationFrame(frame => Math.min(frame + 1, 40));
    }, 700);
    return () => window.clearInterval(intervalId);
  }, [activeRun]);

  // Poll real Cloud Build status for the merged build components until every
  // build reaches a terminal state or the 1h timeout elapses.
  useEffect(() => {
    if (!buildRun) return undefined;
    let cancelled = false;
    let timerId = 0;
    const stop = () => {
      cancelled = true;
      if (timerId) window.clearTimeout(timerId);
    };

    const poll = async () => {
      if (cancelled) return;
      if (Date.now() - buildRun.startedMs >= BUILD_TIMEOUT_MS) {
        setBuildTimedOut(true);
        stop();
        return;
      }
      let updates: Record<string, BuildStatus> = {};
      let fetchFailed = false;
      try {
        // One request for the whole wave instead of one per component.
        updates = await api.getGroupBuildStatuses(
          groupName,
          buildRun.components,
          buildRun.since,
        );
      } catch {
        // Keep ticking, but count the failure so the panel can say the status
        // is stale rather than looking stuck on "waiting for build".
        fetchFailed = true;
      }
      if (cancelled) return;
      setBuildPollFailStreak(prev => (fetchFailed ? prev + 1 : 0));
      setBuildStatuses(prev => ({ ...prev, ...updates }));
      const allTerminal = buildRun.components.every(name => {
        const st = updates[name];
        return st && isTerminalBuild(st.state);
      });
      if (allTerminal) stop();
    };

    // Self-scheduling so the interval can stretch as the run ages.
    const tick = async () => {
      await poll();
      if (cancelled) return;
      const elapsed = Date.now() - buildRun.startedMs;
      timerId = window.setTimeout(tick, nextPollDelay(elapsed));
    };
    tick();
    return stop;
  }, [buildRun, api, groupName]);

  // Mirror build progress to localStorage so it survives a reload.
  useEffect(() => {
    const key = buildRunStorageKey(groupName);
    try {
      if (buildRun) {
        window.localStorage.setItem(
          key,
          JSON.stringify({
            run: buildRun,
            statuses: buildStatuses,
            timedOut: buildTimedOut,
          }),
        );
      } else {
        window.localStorage.removeItem(key);
      }
    } catch {
      // localStorage may be unavailable (private mode / quota); progress just
      // won't persist across reloads in that case.
    }
  }, [groupName, buildRun, buildStatuses, buildTimedOut]);

  const refresh = useCallback(() => setRefreshTick(t => t + 1), []);

  const onMerged = useCallback((componentName: string, prNumber: number) => {
    setData(prev => removePr(prev, componentName, prNumber));
    setSelectedPrKeys(prev => {
      const next = new Set(prev);
      next.delete(prKey(componentName, prNumber));
      return next;
    });
  }, []);

  const onPrRefreshed = useCallback(
    (componentName: string, fresh: PrSummary) => {
      setData(prev => updatePr(prev, componentName, fresh.number, () => fresh));
    },
    [],
  );

  const onApproved = useCallback((componentName: string, prNumber: number) => {
    setData(prev =>
      updatePr(prev, componentName, prNumber, pr => {
        const approvals = pr.approvals + 1;
        // mergeableState is computed by GitHub and stays stale ('blocked')
        // after an optimistic approve until a refetch. Once approvals are
        // satisfied, relax it to 'unknown' so the proactive blocked gate /
        // "N blocked" badge clears without a reload. The real state is
        // reconfirmed on the next refresh, or surfaced as a clear error at
        // merge time if something else still blocks it.
        const mergeableState: PrSummary['mergeableState'] =
          approvals >= pr.requiredApprovals && pr.mergeableState === 'blocked'
            ? 'unknown'
            : pr.mergeableState;
        return { ...pr, approvals, mergeableState };
      }),
    );
  }, []);

  // Merge every PR in one deploy wave (serially, so a mid-wave failure — or a
  // cancel landing mid-wave — stops the rest).
  const mergeWave = useCallback(
    async (wave: DeployRun['waves'][number]): Promise<WaveMergeResult> => {
      const prCount = wave.components.reduce(
        (sum, c) => sum + c.prNumbers.length,
        0,
      );
      setBulkBusy(true);
      setBulkStatus({
        severity: 'info',
        message: `Merging wave ${wave.wave} (${formatCount(prCount, 'PR')})…`,
      });
      try {
        const result = await mergeWavePrs(wave, {
          mergePr: (componentName, prNumber) =>
            api.mergePr(componentName, prNumber),
          onMerged,
          shouldCancel: () => deployCancelRequestedRef.current,
        });
        if (result.outcome === 'merged') {
          setBulkStatus({
            severity: 'info',
            message: `Wave ${wave.wave} merged; watching deploy builds…`,
          });
        } else if (result.outcome === 'cancelled') {
          setBulkStatus({
            severity: 'info',
            message: `Cancelled while merging wave ${wave.wave}: ${formatCount(
              result.mergedCount,
              'PR',
            )} already merged, the rest were not.`,
          });
        } else {
          setBulkStatus({
            severity: 'error',
            message: `Wave ${wave.wave} merge failed: ${actionErrorMessage(
              result.error,
            )}`,
          });
        }
        return result;
      } finally {
        setBulkBusy(false);
      }
    },
    [api, onMerged],
  );

  // Drive the active deploy run: poll Cloud Run for the current wave, and when
  // every component succeeds, merge the next wave; stop on failure/timeout so
  // later waves never deploy.
  useEffect(() => {
    if (!deployRun || deployRun.phase !== 'polling') return undefined;
    const wave = deployRun.waves[deployRun.currentWaveIndex];
    const envs = envsForTarget(deployRun.target);
    const probes = waveProbes(wave, envs, deployRun.skipped);
    // `disposed` is set only by the cleanup (unmount / deployRun change), so it
    // distinguishes teardown from `clearTimer`, which we call to stop ticking
    // once a wave reaches an outcome without bailing out of the advance below.
    let disposed = false;
    let timerId = 0;
    // Guards the terminal transition: if two polls overlap (a slow request
    // outlasting the interval), only the first advances/fails so the next
    // wave is never merged twice.
    let settled = false;
    const clearTimer = () => {
      if (timerId) window.clearTimeout(timerId);
      timerId = 0;
    };

    const poll = async () => {
      if (disposed) return;
      const timedOut =
        Date.now() - deployRun.waveStartedMs >= DEPLOY_TIMEOUT_MS;
      // Batch probes by env: one request per env covers every component in the
      // wave, instead of one request per (component, env).
      const byEnv = new Map<'stg' | 'prd', string[]>();
      for (const { componentName, env } of probes) {
        const list = byEnv.get(env) ?? [];
        list.push(componentName);
        byEnv.set(env, list);
      }
      const updates: Record<string, DeployStatus> = {};
      let anyFetchFailed = false;
      await Promise.all(
        [...byEnv.entries()].map(async ([env, names]) => {
          try {
            const statuses = await api.getGroupDeployStatuses(
              groupName,
              names,
              env,
              deployRun.since,
            );
            for (const [componentName, status] of Object.entries(statuses)) {
              updates[deployStatusKey(componentName, env)] = status;
            }
          } catch {
            // Leave these probes un-updated this tick, but count the failure
            // so the panel can say the status is stale — a persistent fetch
            // failure otherwise looks exactly like "waiting for deploy".
            anyFetchFailed = true;
          }
        }),
      );
      if (disposed) return;
      deployPollFailStreakRef.current = anyFetchFailed
        ? deployPollFailStreakRef.current + 1
        : 0;
      setDeployPollFailStreak(deployPollFailStreakRef.current);
      deployStatusesRef.current = { ...deployStatusesRef.current, ...updates };
      setDeployStatuses(prev => ({ ...prev, ...updates }));

      const outcome = waveOutcome(
        wave,
        envs,
        deployStatusesRef.current,
        timedOut,
        deployRun.skipped,
      );
      if (outcome === 'pending') return;
      if (settled) return;
      settled = true;
      clearTimer();
      if (outcome === 'failed') {
        if (timedOut) setDeployTimedOut(true);
        setDeployRun(prev =>
          prev ? { ...prev, phase: timedOut ? 'timeout' : 'failed' } : prev,
        );
        // A timeout reached while status fetches were failing is a verdict on
        // the polling, not the deploys — say so instead of reporting a plain
        // failure for deploys that may well have succeeded.
        const pollingBroken = timedOut && deployPollFailStreakRef.current >= 2;
        setBulkStatus({
          severity: 'error',
          message: `Wave ${wave.wave} deploy ${
            timedOut ? 'timed out' : 'failed'
          }; stopped before later waves.${
            pollingBroken
              ? ' Status fetches were failing, so the deploys may have ' +
                'succeeded — check Cloud Build directly.'
              : ''
          }`,
        });
        return;
      }
      // Wave succeeded — advance, or finish if it was the last.
      const nextIndex = deployRun.currentWaveIndex + 1;
      if (nextIndex >= deployRun.waves.length) {
        setDeployRun(prev => (prev ? { ...prev, phase: 'done' } : prev));
        setBulkStatus({
          severity: 'success',
          message: `All ${formatCount(
            deployRun.waves.length,
            'wave',
          )} deployed successfully.`,
        });
        return;
      }
      // A cancel that landed while this tick's requests were in flight wins:
      // confirmCancelDeployRun already moved the run to 'cancelled'.
      if (deployCancelRequestedRef.current) return;
      // Browser-clock fallback, captured before the merge — see
      // sinceForMerge(). The merge responses' server-side mergedAt wins.
      const fallbackSince = sinceForMerge();
      const result = await mergeWave(deployRun.waves[nextIndex]);
      // Only bail if torn down during the merge; the setDeployRun guards below
      // safely no-op if the run was dismissed (prev === null).
      if (disposed) return;
      if (result.outcome === 'cancelled') return;
      if (result.outcome === 'failed') {
        setDeployRun(prev => (prev ? { ...prev, phase: 'failed' } : prev));
        return;
      }
      setDeployTimedOut(false);
      const nextSince = sinceFromMergedAt(result.since) ?? fallbackSince;
      // Only advance a run that is still polling: a cancel arriving between the
      // wave's last merge and this update must not be overwritten back to
      // 'polling' (that would resume the run the user just stopped).
      setDeployRun(prev =>
        prev && prev.phase === 'polling'
          ? {
              ...prev,
              currentWaveIndex: nextIndex,
              since: nextSince,
              waveStartedMs: Date.now(),
              phase: 'polling',
            }
          : prev,
      );
    };

    // Self-scheduling so the interval can stretch as the wave ages; `settled`
    // stops it once the wave reaches an outcome.
    const tick = async () => {
      await poll();
      if (disposed || settled) return;
      const elapsed = Date.now() - deployRun.waveStartedMs;
      timerId = window.setTimeout(tick, nextPollDelay(elapsed));
    };
    tick();
    return () => {
      disposed = true;
      clearTimer();
    };
  }, [deployRun, api, mergeWave, groupName]);

  // A stopped run (failed / timeout / cancelled) closes the GATE — nothing
  // more is merged — but the wave's already-triggered builds keep running in
  // Cloud Build. Keep watching just those rows so the panel keeps matching
  // reality: without this, a sibling that was mid-deploy freezes at
  // "deploying" until someone presses Resume, and the skip dialog reasons
  // about stale states. Observation only: statuses are updated, outcomes are
  // never evaluated, nothing advances.
  useEffect(() => {
    if (!deployRun || !canResumeDeployRun(deployRun.phase)) return undefined;
    const wave = deployRun.waves[deployRun.currentWaveIndex];
    const envs = envsForTarget(deployRun.target);
    let disposed = false;
    let timerId = 0;
    const observeStartedMs = Date.now();
    setObserveActive(
      inFlightWaveEntries(
        wave,
        envs,
        deployStatusesRef.current,
        deployRun.skipped,
      ).length > 0,
    );

    const tick = async () => {
      if (disposed) return;
      const probes = inFlightWaveEntries(
        wave,
        envs,
        deployStatusesRef.current,
        deployRun.skipped,
      );
      if (
        probes.length === 0 ||
        Date.now() - observeStartedMs >= OBSERVE_MAX_MS
      ) {
        setObserveActive(false);
        return;
      }
      const byEnv = new Map<'stg' | 'prd', string[]>();
      for (const { componentName, env } of probes) {
        const list = byEnv.get(env) ?? [];
        list.push(componentName);
        byEnv.set(env, list);
      }
      const updates: Record<string, DeployStatus> = {};
      await Promise.all(
        [...byEnv.entries()].map(async ([env, names]) => {
          try {
            const statuses = await api.getGroupDeployStatuses(
              groupName,
              names,
              env,
              deployRun.since,
            );
            for (const [componentName, status] of Object.entries(statuses)) {
              updates[deployStatusKey(componentName, env)] = status;
            }
          } catch {
            // Observation only — a failed tick just means stale rows until
            // the next one.
          }
        }),
      );
      if (disposed) return;
      deployStatusesRef.current = { ...deployStatusesRef.current, ...updates };
      setDeployStatuses(prev => ({ ...prev, ...updates }));
      timerId = window.setTimeout(tick, OBSERVE_POLL_MS);
    };
    // First check after one interval: the main poll just ran when the run
    // stopped, so an immediate re-poll would be redundant.
    timerId = window.setTimeout(tick, OBSERVE_POLL_MS);
    return () => {
      disposed = true;
      if (timerId) window.clearTimeout(timerId);
      setObserveActive(false);
    };
  }, [deployRun, api, groupName]);

  // Mirror deploy progress to localStorage so it survives a reload.
  useEffect(() => {
    const key = deployRunStorageKey(groupName);
    try {
      if (deployRun) {
        window.localStorage.setItem(
          key,
          JSON.stringify({
            run: deployRun,
            statuses: deployStatuses,
            timedOut: deployTimedOut,
          }),
        );
      } else {
        window.localStorage.removeItem(key);
      }
    } catch {
      // localStorage may be unavailable; progress just won't persist.
    }
  }, [groupName, deployRun, deployStatuses, deployTimedOut]);

  // Tick `nowMs` every second while a build or deploy run is in flight so
  // elapsed-time labels advance; stop once terminal (durations then freeze on
  // their build finishTime).
  const deployRunInFlight =
    deployRun?.phase === 'merging' || deployRun?.phase === 'polling';
  const buildRunInFlight =
    !!buildRun &&
    !buildTimedOut &&
    !buildRun.components.every(name => {
      const st = buildStatuses[name];
      return st && isTerminalBuild(st.state);
    });
  const anyRunInFlight = deployRunInFlight || buildRunInFlight || observeActive;
  useEffect(() => {
    if (!anyRunInFlight) return undefined;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [anyRunInFlight]);

  if (state.loading && !data) {
    return (
      <Content>
        <Progress />
      </Content>
    );
  }

  if (state.error && !data) {
    return (
      <Content>
        <Header groupName={groupName} onRefresh={refresh} />
        <RenderError error={state.error} />
      </Content>
    );
  }

  const components = data?.components ?? [];

  if (components.length === 0) {
    return (
      <Content>
        <Header groupName={groupName} onRefresh={refresh} />
        <EmptyState
          title="No owned components"
          description={`No Components have ${groupName} as their spec.owner.`}
          missing="content"
        />
      </Content>
    );
  }

  // Deployment-group selection is exclusive, like category: bulk operations
  // (and the wave deploy loop) only ever act on the selected group's
  // components — though a component may belong to several groups and thus
  // show up under each. "all" (the default) ignores groups and shows the
  // team's whole fleet. The list is derived from all owned components (not
  // the category-filtered set) so the selector doesn't appear/disappear when
  // toggling category. A stale persisted selection (group renamed/removed)
  // falls back to "all".
  const deployGroups = distinctDeployGroups(
    components.map(c => c.deployGroups),
  );
  const activeDeployGroup =
    deployGroup === ALL_DEPLOY_GROUPS || deployGroups.includes(deployGroup)
      ? deployGroup
      : ALL_DEPLOY_GROUPS;

  // Category is intentionally exclusive: Cloud Run, Terraform, or — when a
  // deploy group deliberately ships both kinds together — Both.
  const filtered: ComponentPrs[] = components
    .filter(c => matchesDeployGroup(c.deployGroups, activeDeployGroup))
    .filter(c => matchesCategory(c.projectName, filter.category))
    // Maintenance-mode components are included by default; unticking the
    // filter bar's "Include maintenance" checkbox removes them from the view
    // and therefore from bulk operations and the wave deploy loop.
    .filter(c =>
      matchesMaintenance(c.maintenanceMode, filter.includeMaintenance),
    )
    .map(c => ({ ...c }));

  // Components are shown in the fixed MergeOps flow: Build, then deploy waves
  // (ascending) derived from each component's own `wave` value.
  const sections = buildWaveSections(filtered, deployTarget);
  const stgDeploySections = buildWaveSections(filtered, 'stg').filter(
    section => section.kind === 'deploy',
  );
  const prdDeploySections = buildWaveSections(filtered, 'prd').filter(
    section => section.kind === 'deploy',
  );
  const allDeploySections = buildWaveSections(filtered, 'all').filter(
    section => section.kind === 'deploy',
  );
  const buildSection = sections.find(section => section.kind === 'build');
  const buildComponents = buildSection?.components ?? [];
  const buildPrCount = countPrs(buildComponents);
  const deploySections = sections.filter(section => section.kind === 'deploy');
  const deployPrCount = deploySections.reduce(
    (sum, section) => sum + countPrs(section.components),
    0,
  );
  const stgDeployPrCount = stgDeploySections.reduce(
    (sum, section) => sum + countPrs(section.components),
    0,
  );
  const prdDeployPrCount = prdDeploySections.reduce(
    (sum, section) => sum + countPrs(section.components),
    0,
  );
  const allDeployPrCount = allDeploySections.reduce(
    (sum, section) => sum + countPrs(section.components),
    0,
  );
  const previewDeploySections = buildPreviewDeploySections(
    filtered,
    deployTarget,
  );

  // Render-only lists that keep owned components even when they have no PRs, so
  // each owned component's area still shows (rendering "No PR"). Counts, bulk
  // targets and selection stay driven by the PR-only sections above.
  const buildRenderComponents = sortComponents(
    filtered.map(c =>
      withItems(
        c,
        c.items.filter(pr => pr.prType === 'tag'),
      ),
    ),
  );
  const deployRenderSections = deploySectionsByWave(
    filtered,
    deployTarget,
    'deploy',
    true,
  );
  const hasStepContent =
    selectedFlowStep === 'build'
      ? buildRenderComponents.length > 0
      : deployRenderSections.length > 0;

  const totalPrs = sections.reduce(
    (sectionSum, section) => sectionSum + countPrs(section.components),
    0,
  );
  const categoryLabel = {
    cloudrun: 'Cloud Run',
    terraform: 'Terraform',
    both: 'Cloud Run + Terraform',
  }[filter.category];
  const maintenanceCount = filtered.filter(c => c.maintenanceMode).length;
  // Terraform components apply (terraform apply) rather than deploy, so the
  // deploy-step wording follows what the view contains: "Apply" for Terraform
  // only, "Deploy" otherwise (under Both, a run promotes Cloud Run components
  // and applies Terraform together — the captions spell that out).
  const hasTerraform = filtered.some(c => isTerraform(c.projectName));
  const hasCloudRun = filtered.some(c => !isTerraform(c.projectName));
  let deployKind: 'cloudrun' | 'terraform' | 'mixed' = 'cloudrun';
  if (hasTerraform) deployKind = hasCloudRun ? 'mixed' : 'terraform';
  const deployVerb = deployKind === 'terraform' ? 'Apply' : 'Deploy';
  const buildTargets = targetsFromComponents(buildComponents);
  const deployTargets = targetsFromSections(deploySections);
  const stgDeployTargets = targetsFromSections(stgDeploySections);
  const prdDeployTargets = targetsFromSections(prdDeploySections);
  const allDeployTargets = targetsFromSections(allDeploySections);
  const buildBlockedCount = blockedTargetCount(buildTargets);
  const deployBlockedCount = blockedTargetCount(deployTargets);
  const stgDeployBlockedCount = blockedTargetCount(stgDeployTargets);
  const prdDeployBlockedCount = blockedTargetCount(prdDeployTargets);
  const allDeployBlockedCount = blockedTargetCount(allDeployTargets);
  let selectedModeLabel = 'Build';
  if (selectedFlowStep !== 'build') {
    selectedModeLabel = `${deployVerb} ${deployTargetLabel(selectedFlowStep)}`;
  }
  let visibleSections: WaveSection[] = [];
  if (selectedFlowStep === 'build') {
    visibleSections = buildSection ? [buildSection] : [];
  } else {
    visibleSections = deploySections;
  }
  const selectedTargets =
    selectedFlowStep === 'build' ? buildTargets : deployTargets;
  const selectedOperation = selectedFlowStep === 'build' ? 'build' : 'deploy';
  const checkedTargets = selectedTargets.filter(target =>
    selectedPrKeys.has(prKey(target.componentName, target.pr.number)),
  );
  const checkedCount = checkedTargets.length;
  const checkedCanMerge = canMergeTargets(checkedTargets);
  const allVisibleChecked =
    selectedTargets.length > 0 && checkedCount === selectedTargets.length;

  const setPrSelected = (
    componentName: string,
    pr: PrSummary,
    selected: boolean,
  ) => {
    const key = prKey(componentName, pr.number);
    setSelectedPrKeys(prev => {
      const next = new Set(prev);
      if (selected) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  const setAllVisibleSelected = (selected: boolean) => {
    setSelectedPrKeys(prev => {
      const next = new Set(prev);
      for (const target of selectedTargets) {
        const key = prKey(target.componentName, target.pr.number);
        if (selected) {
          next.add(key);
        } else {
          next.delete(key);
        }
      }
      return next;
    });
  };

  const isComponentDiffExpanded = (componentName: string): boolean =>
    componentDiffExpanded[componentName] ?? diffExpanded;

  const setComponentDiffExpandedValue = (
    componentName: string,
    expanded: boolean,
  ) => {
    setComponentDiffExpanded(prev => ({
      ...prev,
      [componentName]: expanded,
    }));
  };

  const selectDeployTarget = (target: DeployTarget) => {
    setDeployTarget(target);
    setSelectedFlowStep(target);
  };

  const approveTargets = async (targets: PrTarget[], label: string) => {
    if (targets.length === 0) return;
    setBulkBusy(true);
    setBulkStatus({ severity: 'info', message: `${label} in progress.` });
    try {
      for (const target of targets) {
        await api.approvePr(target.componentName, target.pr.number);
        onApproved(target.componentName, target.pr.number);
      }
      setBulkStatus({
        severity: 'success',
        message: `${label} completed for ${formatCount(targets.length, 'PR')}.`,
      });
    } catch (err) {
      setBulkStatus({ severity: 'error', message: actionErrorMessage(err) });
    } finally {
      setBulkBusy(false);
    }
  };

  /**
   * Merges each target in turn; null when the run stopped on an error. `since`
   * is the earliest server-reported merge time (see ApproveOrMergeResult),
   * for the Cloud Build tracking that follows a build-step merge.
   */
  const mergeTargets = async (
    targets: PrTarget[],
    label: string,
  ): Promise<{ outcomes: MergeOutcome[]; since: string | null } | null> => {
    if (targets.length === 0) return null;
    setBulkBusy(true);
    setBulkStatus({ severity: 'info', message: `${label} in progress.` });
    const outcomes: MergeOutcome[] = [];
    let earliestMergedAt: string | null = null;
    try {
      for (const target of targets) {
        const result = await api.mergePr(
          target.componentName,
          target.pr.number,
          target.pr.title,
        );
        earliestMergedAt = minIso(earliestMergedAt, result.mergedAt ?? null);
        outcomes.push({
          componentName: target.componentName,
          prNumber: target.pr.number,
          alreadyMerged: Boolean(result.alreadyMerged),
        });
        onMerged(target.componentName, target.pr.number);
      }
      setBulkStatus({
        severity: 'success',
        message: mergeSummaryMessage(label, outcomes),
      });
      return { outcomes, since: earliestMergedAt };
    } catch (err) {
      setBulkStatus({ severity: 'error', message: actionErrorMessage(err) });
      return null;
    } finally {
      setBulkBusy(false);
    }
  };

  // After build (tag) PRs merge, their run_kit Cloud Builds fire. Start polling
  // the real build status for the affected components (1h timeout).
  // `since` is captured by the caller BEFORE the merge (see sinceForMerge()),
  // since the merge triggers the run_kit build immediately.
  const startBuildPolling = (componentNames: string[], since: string) => {
    // Terraform (-tf) components are excluded: their tag PR merge triggers no
    // run_kit image build, so a tracked row would sit on "waiting for build"
    // until the 1h timeout. Their actual work (tfez apply) is watched by the
    // DEPLOY progress, not here. Match on projectName — that is what carries
    // the -tf convention (componentName usually equals it, but the project
    // annotation can override).
    const byName = new Map(
      (data?.components ?? []).map(c => [c.componentName, c.projectName]),
    );
    const trackable = componentNames.filter(
      name => !isTerraform(byName.get(name) ?? name),
    );
    if (trackable.length === 0) return;
    setBuildStatuses({});
    setBuildTimedOut(false);
    setBuildPollFailStreak(0);
    setBuildRun({
      since,
      startedMs: Date.now(),
      components: trackable,
    });
  };

  // Hide the progress panel (the build keeps running on Cloud Build); also
  // clears the persisted run so it doesn't return on reload.
  const dismissBuildRun = () => {
    setBuildRun(null);
    setBuildStatuses({});
    setBuildTimedOut(false);
  };

  // Merge wave 0 of the selected deploy target and start watching; the poll
  // driver then auto-advances through the remaining waves on success.
  const startDeployWaves = async () => {
    const waves = buildDeployRunWaves(deploySections, deployTarget);
    if (waves.length === 0) return;
    deployStatusesRef.current = {};
    setDeployStatuses({});
    setDeployTimedOut(false);
    deployPollFailStreakRef.current = 0;
    setDeployPollFailStreak(0);
    deployCancelRequestedRef.current = false;
    // Browser-clock fallback, captured before the merge — see sinceForMerge().
    // The merge responses' server-side mergedAt is preferred when present.
    const fallbackSince = sinceForMerge();
    const result = await mergeWave(waves[0]);
    if (result.outcome !== 'merged') return;
    setDeployRun({
      target: deployTarget,
      waves,
      currentWaveIndex: 0,
      since: sinceFromMergedAt(result.since) ?? fallbackSince,
      waveStartedMs: Date.now(),
      phase: 'polling',
    });
  };

  const dismissDeployRun = () => {
    deployStatusesRef.current = {};
    setDeployRun(null);
    setDeployStatuses({});
    setDeployTimedOut(false);
    deployPollFailStreakRef.current = 0;
    setDeployPollFailStreak(0);
    deployCancelRequestedRef.current = false;
    setCancelConfirmOpen(false);
  };

  // Guards a stopped run leaves set: the elapsed-timeout flag, the poll-failure
  // streak, and the cancel latch. Both Resume and a skip-driven restart have to
  // clear all three, or the run stops again on the stale state.
  const resetDeployRunGuards = () => {
    setDeployTimedOut(false);
    deployPollFailStreakRef.current = 0;
    setDeployPollFailStreak(0);
    deployCancelRequestedRef.current = false;
    setCancelConfirmOpen(false);
  };

  // Resume a stopped (failed / timed-out / cancelled) run: back to polling the
  // wave it stopped on, with a fresh timeout window. The normal poll driver
  // does the rest — a deploy that succeeded meanwhile (e.g. a manually re-run
  // Cloud Build) advances the run, and PRs a mid-wave stop left unmerged are
  // merged by the advance itself (already-merged PRs are skipped, and their
  // original merge time keeps `since` early enough to find their builds).
  const resumeDeployRun = () => {
    if (!deployRun) return;
    const resumed = resumedDeployRun(deployRun, Date.now());
    if (!resumed) return;
    resetDeployRunGuards();
    setDeployRun(resumed);
    setBulkStatus({
      severity: 'info',
      message:
        `Resumed the deploy run at wave ` +
        `${resumed.waves[resumed.currentWaveIndex].wave}: re-checking its ` +
        `deploys, then advancing as usual.`,
    });
  };

  // Skipping takes one (component, env) out of the gate and, when the run has
  // already stopped on it, puts the run back to polling in the same action —
  // otherwise the user would have to press Skip and then Resume for what reads
  // as one decision. The audit call goes first: if it fails (not a member of
  // the owning group, blank reason) nothing is skipped.
  const confirmSkipDeploy = async () => {
    if (!deployRun || !skipTarget) return;
    const reason = skipReason.trim();
    if (!reason) return;
    setSkipSubmitting(true);
    try {
      await api.skipDeploy(skipTarget.componentName, {
        env: skipTarget.env,
        wave: skipTarget.wave,
        state: skipTarget.state,
        reason,
      });
    } catch (error) {
      setSkipSubmitting(false);
      setBulkStatus({
        severity: 'error',
        message: `Could not skip ${skipTarget.projectName} (${
          skipTarget.env
        }): ${actionErrorMessage(error)}`,
      });
      return;
    }
    const entry: DeploySkip = {
      componentName: skipTarget.componentName,
      env: skipTarget.env,
      state: skipTarget.state,
      reason,
      at: new Date().toISOString(),
    };
    const withSkip: DeployRun = {
      ...deployRun,
      skipped: [...(deployRun.skipped ?? []), entry],
    };
    const resumed = canResumeDeployRun(withSkip.phase)
      ? resumedDeployRun(withSkip, Date.now())
      : null;
    if (resumed) resetDeployRunGuards();
    setDeployRun(resumed ?? withSkip);
    setSkipSubmitting(false);
    setSkipTarget(null);
    setSkipReason('');
    setBulkStatus({
      severity: 'info',
      message: resumed
        ? `Skipped ${skipTarget.projectName} (${skipTarget.env}) and resumed ` +
          `wave ${withSkip.waves[withSkip.currentWaveIndex].wave}.`
        : `Skipped ${skipTarget.projectName} (${skipTarget.env}); the wave ` +
          `no longer waits for it.`,
    });
  };

  // Cancel stops the run immediately: no later wave is merged and the panel
  // stops watching. The PRs already merged can't be taken back, so the deploy
  // builds they triggered keep running — the dialog says so before confirming.
  const confirmCancelDeployRun = () => {
    setCancelConfirmOpen(false);
    deployCancelRequestedRef.current = true;
    setDeployRun(prev => (prev ? { ...prev, phase: 'cancelled' } : prev));
    setBulkStatus({
      severity: 'info',
      message:
        'Deploy run cancelled; later waves were not merged. The deploy builds already started keep running.',
    });
  };

  // While a deploy run is mid-flight, manual merges are blocked so waves stay
  // strictly ordered (a wave can't be jumped ahead of the running one).
  const deployRunActive =
    !!deployRun &&
    (deployRun.phase === 'merging' || deployRun.phase === 'polling');

  // Why the bulk action buttons are disabled, surfaced on hover below.
  const approveAllReason = approveAllDisabledReason({
    ownsGroup,
    bulkBusy,
    selectedCount: selectedTargets.length,
  });
  const mergeCheckedReason = mergeCheckedDisabledReason({
    ownsGroup,
    bulkBusy,
    checkedCount,
    checkedCanMerge,
    isDeployStep: selectedFlowStep !== 'build',
    deployRunActive,
  });
  const deployWavesReason = deployWavesDisabledReason({
    ownsGroup,
    bulkBusy,
    deployRunActive,
    deploySectionCount: deploySections.length,
    deployBlockedCount,
  });

  const confirmRun = async () => {
    // Build dry-run was removed (real Cloud Build polling replaces it), so the
    // preview/simulation path only handles deploy.
    if (previewRun?.operation === 'deploy') {
      setActiveRun({
        mode: 'deploy',
        executionMode: previewRun.executionMode,
        buildComponents: [],
        deploySections,
      });
    }
    setPreviewRun(null);
  };

  return (
    <Content>
      <Header groupName={groupName} onRefresh={refresh} />
      <MergeOpsFilterBar
        value={filter}
        onChange={setFilter}
        viewPattern={viewPattern}
        onViewPatternChange={setViewPattern}
        deployGroups={deployGroups}
        deployGroup={activeDeployGroup}
        onDeployGroupChange={setDeployGroup}
      />
      <Typography variant="body2" className={classes.summary}>
        {deployGroups.length > 1 ? `${activeDeployGroup} · ` : ''}
        {categoryLabel} · {formatCount(filtered.length, 'component')}
        {maintenanceCount > 0 && ` (${maintenanceCount} in maintenance)`} ·{' '}
        {formatCount(totalPrs, 'open MergeOps PR')}
      </Typography>

      <Box className={classes.flowPanel}>
        <Box
          className={`${classes.flowRail} ${
            viewPattern === 'combined' ? classes.flowRailCombined : ''
          }`}
        >
          <FlowNode
            title="Build"
            caption="Create and approve release tags"
            prCount={buildPrCount}
            blockedCount={buildBlockedCount}
            selected={selectedFlowStep === 'build'}
            onClick={() => setSelectedFlowStep('build')}
          />
          <FlowArrow />
          {viewPattern === 'split' ? (
            <>
              <FlowNode
                title={`${deployVerb} Stg`}
                caption={
                  {
                    cloudrun: 'Promote validated tags to staging',
                    terraform: 'Apply terraform to staging',
                    mixed: 'Promote tags and apply terraform to staging',
                  }[deployKind]
                }
                prCount={stgDeployPrCount}
                waveCount={stgDeploySections.length}
                blockedCount={stgDeployBlockedCount}
                selected={selectedFlowStep === 'stg'}
                onClick={() => selectDeployTarget('stg')}
              />
              <FlowArrow />
              <FlowNode
                title={`${deployVerb} Prd`}
                caption={
                  {
                    cloudrun: 'Release staged changes to production',
                    terraform: 'Apply terraform to production',
                    mixed: 'Release apps and apply terraform to production',
                  }[deployKind]
                }
                prCount={prdDeployPrCount}
                waveCount={prdDeploySections.length}
                blockedCount={prdDeployBlockedCount}
                selected={selectedFlowStep === 'prd'}
                onClick={() => selectDeployTarget('prd')}
              />
            </>
          ) : (
            <FlowNode
              title={`${deployVerb} Stg & Prd`}
              caption={
                deployKind === 'terraform'
                  ? 'Apply staging and production together'
                  : 'Shift staging and production together'
              }
              prCount={allDeployPrCount}
              waveCount={allDeploySections.length}
              blockedCount={allDeployBlockedCount}
              selected={selectedFlowStep === 'all'}
              onClick={() => selectDeployTarget('all')}
            />
          )}
        </Box>
      </Box>

      <E2eResultsPanel results={e2eResults} />

      {/* A failed refresh with data on screen would otherwise be silent: the
          list quietly stays at its last loaded state while looking current. */}
      {state.error && data && (
        <Alert severity="warning" className={classes.bulkStatus}>
          Refreshing the list failed; showing the last loaded state.{' '}
          {actionErrorMessage(state.error)}
        </Alert>
      )}

      {!ownsGroup && (
        <Alert severity="info" className={classes.bulkStatus}>
          You are not a member of {groupName}; MergeOps actions (approve / merge
          / deploy) are read-only for this team.
        </Alert>
      )}

      {bulkStatus && (
        <Alert severity={bulkStatus.severity} className={classes.bulkStatus}>
          {bulkStatus.message}
        </Alert>
      )}

      {buildRun && selectedFlowStep === 'build' && (
        <Box className={classes.progressPanel}>
          <Box className={classes.progressHeader}>
            <Typography className={classes.progressTitle}>
              Build progress
            </Typography>
            <Typography className={classes.phaseMeta}>
              {buildTimedOut
                ? 'stopped after 1h timeout'
                : `polling Cloud Build${
                    buildPollFailStreak >= 2
                      ? ` · status fetch failing ×${buildPollFailStreak}, showing stale state`
                      : ''
                  }`}
            </Typography>
            <Button
              size="small"
              onClick={dismissBuildRun}
              className={classes.progressDismiss}
            >
              Dismiss
            </Button>
          </Box>
          <Box className={classes.progressRows}>
            {buildRun.components.map(name => {
              const status = buildStatuses[name];
              const stateValue: BuildState =
                buildTimedOut && (!status || !isTerminalBuild(status.state))
                  ? 'timeout'
                  : status?.state ?? 'none';
              const dotClassByKind: Record<
                ReturnType<typeof buildDotKind>,
                string
              > = {
                running: classes.runningDot,
                done: classes.completedDot,
                failed: classes.failedDot,
                idle: '',
              };
              const dotKind = buildDotKind(stateValue);
              const dotClass = dotClassByKind[dotKind];
              // With no build of its own, the row falls back to the build that
              // ran just before the run — the only thing that explains the wait.
              const preceding = status?.precedingBuild ?? null;
              const triggerName =
                status?.triggerName ?? preceding?.triggerName ?? null;
              return (
                <Box
                  key={name}
                  className={[
                    classes.progressRow,
                    dotKind === 'failed' ? classes.progressRowFailed : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <Typography className={classes.progressProduct}>
                    {name}
                  </Typography>
                  <Typography
                    className={classes.progressPr}
                    title={triggerName ?? undefined}
                  >
                    <Link
                      href={
                        status?.logUrl ??
                        preceding?.logUrl ??
                        cloudBuildUrl(name)
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Cloud Build
                    </Link>
                    {triggerName ? ` · ${triggerName}` : ''}
                  </Typography>
                  <span className={classes.progressStatus}>
                    <span
                      className={[classes.statusDot, dotClass]
                        .filter(Boolean)
                        .join(' ')}
                    />
                    {buildStateLabel(stateValue)}
                    {preceding && (
                      <span className={classes.progressElapsed}>
                        {precedingBuildNote(preceding)}
                      </span>
                    )}
                    {(() => {
                      const start = status?.startTime
                        ? Date.parse(status.startTime)
                        : buildRun.startedMs;
                      const end = status?.finishTime
                        ? Date.parse(status.finishTime)
                        : nowMs;
                      return (
                        <span className={classes.progressElapsed}>
                          {formatDuration(end - start)}
                          <TriggeredAt createTime={status?.createTime} />
                        </span>
                      );
                    })()}
                  </span>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {deployRun && selectedFlowStep !== 'build' && (
        <Box
          className={[
            classes.progressPanel,
            deployRun.phase === 'failed' || deployRun.phase === 'timeout'
              ? classes.progressPanelFailed
              : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <Box className={classes.progressHeader}>
            <Typography className={classes.progressTitle}>
              Deploy progress
            </Typography>
            <Typography className={classes.phaseMeta}>
              {(() => {
                if (deployRun.phase === 'done') {
                  const n = deployRun.skipped?.length ?? 0;
                  return n === 0
                    ? 'all waves deployed'
                    : `all waves deployed — ${n} skipped by hand`;
                }
                if (deployRun.phase === 'failed')
                  return 'stopped — deploy failed';
                if (deployRun.phase === 'timeout') return 'stopped — timed out';
                if (deployRun.phase === 'cancelled') {
                  return 'stopped — cancelled; later waves were not merged';
                }
                return `watching Cloud Build · wave ${
                  deployRun.currentWaveIndex + 1
                }/${deployRun.waves.length}${
                  deployPollFailStreak >= 2
                    ? ` · status fetch failing ×${deployPollFailStreak}, showing stale state`
                    : ''
                }`;
              })()}
            </Typography>
            {canResumeDeployRun(deployRun.phase) && (
              <Tooltip
                title={
                  !ownsGroup
                    ? `You are not a member of ${groupName}.`
                    : 'Go back to watching the wave this run stopped on, then advance as usual. A deploy that succeeded meanwhile (e.g. a re-run Cloud Build) passes the wave; PRs the stop left unmerged are merged again. If a deploy build itself failed, re-run it in Cloud Build first — otherwise the run stops on the same failure.'
                }
              >
                <span>
                  <Button
                    size="small"
                    color="primary"
                    disabled={!ownsGroup || bulkBusy}
                    onClick={resumeDeployRun}
                    className={classes.progressDismiss}
                  >
                    Resume
                  </Button>
                </span>
              </Tooltip>
            )}
            <Button
              size="small"
              onClick={dismissDeployRun}
              className={classes.progressDismiss}
            >
              {deployRunActive ? 'Stop' : 'Dismiss'}
            </Button>
          </Box>
          <Box>
            {deployRun.waves.map((wave, waveIndex) => {
              const envs = envsForTarget(deployRun.target);
              const future = waveIndex > deployRun.currentWaveIndex;
              const isCurrent = waveIndex === deployRun.currentWaveIndex;
              const dotClassByKind: Record<
                ReturnType<typeof deployDotKind>,
                string
              > = {
                running: classes.runningDot,
                done: classes.completedDot,
                failed: classes.failedDot,
                idle: '',
              };
              return (
                <Box
                  key={wave.wave}
                  className={[
                    classes.progressPhaseGroup,
                    // run は失敗した wave で止まるので、停止中の現在 wave が
                    // そのまま「どこで落ちたか」になる
                    isCurrent &&
                    (deployRun.phase === 'failed' ||
                      deployRun.phase === 'timeout')
                      ? classes.progressPhaseGroupFailed
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <Box className={classes.progressPhaseRail}>
                    <span className={classes.deployPhaseIndex}>
                      {wave.wave}
                    </span>
                    Wave {wave.wave}
                  </Box>
                  <Box className={classes.progressRows}>
                    {wave.components.flatMap(component =>
                      envs.map(env => {
                        // `dummy/` placeholder env: not deployed (no
                        // deploy_kit trigger) — excluded from gating/polling;
                        // show it as skipped instead of "waiting for deploy".
                        if (component.dummyEnvs?.includes(env)) {
                          return (
                            <Box
                              key={`${component.componentName}:${env}`}
                              className={classes.progressRow}
                            >
                              <Typography className={classes.progressProduct}>
                                {component.projectName}
                              </Typography>
                              <Typography className={classes.progressPr}>
                                Cloud Build {env}
                              </Typography>
                              <span className={classes.progressStatus}>
                                <span className={classes.statusDot} />
                                skipped (not deployed)
                              </span>
                            </Box>
                          );
                        }
                        // A human took this (component, env) out of the gate
                        // (ADR 0003) — excluded from gating/polling like a
                        // dummy env, but shown as a deliberate decision with
                        // the state it had and the reason on hover.
                        const skipEntry = deployRun.skipped?.find(
                          s =>
                            s.componentName === component.componentName &&
                            s.env === env,
                        );
                        if (skipEntry) {
                          // Keep the Cloud Build link: the point of looking at
                          // a skipped row afterwards is the FAILED build's log.
                          // The last polled status still holds its logUrl (the
                          // pair stops being polled once skipped); fall back to
                          // the trigger-filtered console like a normal row.
                          const skippedStatus =
                            deployStatuses[
                              deployStatusKey(component.componentName, env)
                            ];
                          return (
                            <Box
                              key={`${component.componentName}:${env}`}
                              className={classes.progressRow}
                            >
                              <Typography className={classes.progressProduct}>
                                {component.projectName}
                              </Typography>
                              <Typography
                                className={classes.progressPr}
                                title={skippedStatus?.triggerName ?? undefined}
                              >
                                <Link
                                  href={
                                    skippedStatus?.logUrl ??
                                    (isTerraform(component.projectName)
                                      ? terraformApplyBuildUrl(
                                          component.projectName,
                                          env,
                                        )
                                      : deployBuildUrl(
                                          component.projectName,
                                          env,
                                        ))
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  Cloud Build {env}
                                </Link>
                              </Typography>
                              <span
                                className={classes.progressStatus}
                                title={skipEntry.reason}
                              >
                                <span className={classes.statusDot} />
                                skipped by hand ({skipEntry.state})
                              </span>
                            </Box>
                          );
                        }
                        const status =
                          deployStatuses[
                            deployStatusKey(component.componentName, env)
                          ];
                        let stateValue = status?.state ?? 'none';
                        if (
                          deployTimedOut &&
                          isCurrent &&
                          !isTerminalDeploy(stateValue)
                        ) {
                          stateValue = 'timeout';
                        }
                        const dotKind = deployDotKind(stateValue);
                        const dotClass = dotClassByKind[dotKind];
                        return (
                          <Box
                            key={`${component.componentName}:${env}`}
                            className={[
                              classes.progressRow,
                              dotKind === 'failed'
                                ? classes.progressRowFailed
                                : '',
                            ]
                              .filter(Boolean)
                              .join(' ')}
                          >
                            <Typography className={classes.progressProduct}>
                              {component.projectName}
                            </Typography>
                            <Typography
                              className={classes.progressPr}
                              title={status?.triggerName ?? undefined}
                            >
                              <Link
                                href={
                                  status?.logUrl ??
                                  (isTerraform(component.projectName)
                                    ? terraformApplyBuildUrl(
                                        component.projectName,
                                        env,
                                      )
                                    : deployBuildUrl(
                                        component.projectName,
                                        env,
                                      ))
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Cloud Build {env}
                              </Link>
                              {status?.triggerName
                                ? ` · ${status.triggerName}`
                                : ''}
                            </Typography>
                            <span className={classes.progressStatus}>
                              <span
                                className={[classes.statusDot, dotClass]
                                  .filter(Boolean)
                                  .join(' ')}
                              />
                              {(() => {
                                if (!future)
                                  return deployStateLabel(stateValue);
                                // Waves past the cancel point never merge.
                                return deployRun.phase === 'cancelled'
                                  ? 'cancelled'
                                  : 'queued';
                              })()}
                              {(() => {
                                if (future) return null;
                                const elapsed = rowElapsedMs(
                                  status,
                                  deployRun.waveStartedMs,
                                  isCurrent,
                                  nowMs,
                                );
                                return elapsed === null ? null : (
                                  <span className={classes.progressElapsed}>
                                    {formatDuration(elapsed)}
                                    <TriggeredAt
                                      createTime={status?.createTime}
                                    />
                                  </span>
                                );
                              })()}
                            </span>
                            {/* Skip appears only once the run has STOPPED on
                                this wave (failed / timeout / cancelled): while
                                the run is still polling, offering Skip invites
                                cutting off deploys that are about to succeed.
                                For a stuck row, the path is Stop → Skip — the
                                skip then puts the run back to polling. */}
                            {canResumeDeployRun(deployRun.phase) &&
                              isCurrent &&
                              stateValue !== 'success' &&
                              !isDeploySkipped(
                                deployRun.skipped,
                                component.componentName,
                                env,
                              ) && (
                                <Button
                                  size="small"
                                  className={classes.skipButton}
                                  disabled={!ownsGroup || skipSubmitting}
                                  onClick={() => {
                                    setSkipReason('');
                                    setSkipTarget({
                                      componentName: component.componentName,
                                      projectName: component.projectName,
                                      env,
                                      state: stateValue,
                                      wave: wave.wave,
                                    });
                                  }}
                                >
                                  Skip
                                </Button>
                              )}
                          </Box>
                        );
                      }),
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {activeRun && selectedFlowStep !== 'build' && (
        <Box className={classes.progressPanel}>
          <Box className={classes.progressHeader}>
            <Typography className={classes.progressTitle}>
              Deploy progress (dry run)
            </Typography>
            <Typography className={classes.phaseMeta}>
              dry-run simulation
            </Typography>
          </Box>

          {activeRun.mode === 'deploy' && (
            <Box>
              {activeRun.deploySections.map((section, index) => (
                <Box key={section.key} className={classes.progressPhaseGroup}>
                  <Box className={classes.progressPhaseRail}>
                    <span className={classes.deployPhaseIndex}>
                      {section.wave}
                    </span>
                    Wave {section.wave}
                  </Box>
                  <Box className={classes.progressRows}>
                    {section.components.flatMap(component =>
                      component.items.map(pr => {
                        const localFrame = simulationFrame - index * 5;
                        const status = simulationStatus(localFrame, 5);
                        return (
                          <Box key={pr.number} className={classes.progressRow}>
                            <Typography className={classes.progressProduct}>
                              {component.projectName}
                            </Typography>
                            <Typography className={classes.progressPr}>
                              #{pr.number} {pr.title}
                              {deployTargetEnvs(pr).map(env => (
                                <span key={env}>
                                  {' '}
                                  ·{' '}
                                  <Link
                                    href={cloudDeployUrl(env)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    Cloud Deploy knowledgework-{env}
                                  </Link>
                                </span>
                              ))}
                            </Typography>
                            <span className={classes.progressStatus}>
                              <span
                                className={[
                                  classes.statusDot,
                                  status === 'running'
                                    ? classes.runningDot
                                    : '',
                                  status === 'completed'
                                    ? classes.completedDot
                                    : '',
                                ]
                                  .filter(Boolean)
                                  .join(' ')}
                              />
                              {statusLabel(status)}
                            </span>
                          </Box>
                        );
                      }),
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}

      <Box className={classes.detailsHeader}>
        <FormControlLabel
          className={classes.detailsTitleGroup}
          disabled={selectedTargets.length === 0 || bulkBusy}
          control={
            <Checkbox
              checked={allVisibleChecked}
              indeterminate={checkedCount > 0 && !allVisibleChecked}
              onChange={(_, checked) => setAllVisibleSelected(checked)}
              inputProps={{ 'aria-label': 'Select all PRs' }}
            />
          }
          label={
            <Typography className={classes.detailsLabel}>
              Open MergeOps PRs - {selectedModeLabel}
              {checkedCount > 0 ? ` (${checkedCount} selected)` : ''}
            </Typography>
          }
        />
        <Box className={classes.detailsActions}>
          <Box className={classes.actionGroup}>
            <Typography className={classes.actionGroupLabel}>Review</Typography>
            <Box className={classes.actionGroupButtons}>
              <Button
                variant="outlined"
                className={classes.detailsActionButton}
                // Purely a view toggle — keep it usable while a bulk
                // approve/merge is in flight (bulkBusy) so long-running
                // Approve All doesn't lock the diff open.
                disabled={visibleSections.length === 0}
                onClick={() => {
                  setDiffExpanded(expanded => !expanded);
                  setComponentDiffExpanded({});
                }}
              >
                {diffExpanded ? 'Collapse Detail' : 'Show Detail'}
              </Button>
            </Box>
          </Box>

          <Box className={classes.actionGroup}>
            <Typography className={classes.actionGroupLabel}>
              Selection
            </Typography>
            <Box className={classes.actionGroupButtons}>
              <Tooltip
                title={approveAllReason ?? ''}
                disableHoverListener={!approveAllReason}
              >
                <span>
                  <Button
                    color="primary"
                    variant="contained"
                    className={classes.detailsActionButton}
                    disabled={approveAllReason !== null}
                    onClick={() =>
                      approveTargets(
                        selectedTargets,
                        `Approve ${selectedOperation}`,
                      )
                    }
                  >
                    Approve All
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={mergeCheckedReason ?? ''}
                disableHoverListener={!mergeCheckedReason}
              >
                <span>
                  <Button
                    color="primary"
                    variant="contained"
                    className={`${classes.detailsActionButton} ${classes.mergeActionButton}`}
                    disabled={mergeCheckedReason !== null}
                    onClick={async () => {
                      // Capture before merge: onMerged removes the PRs from
                      // state, and `since` must precede the build the merge
                      // triggers. Browser-clock fallback only — the merge
                      // responses' server-side mergedAt wins.
                      const merged = [...checkedTargets];
                      const isBuild = selectedFlowStep === 'build';
                      const fallbackSince = sinceForMerge();
                      const result = await mergeTargets(
                        merged,
                        `Merge checked ${selectedOperation}`,
                      );
                      if (result && isBuild) {
                        startBuildPolling(
                          componentsToTrack(result.outcomes),
                          sinceFromMergedAt(result.since ?? undefined) ??
                            fallbackSince,
                        );
                      }
                    }}
                  >
                    Merge Checked
                  </Button>
                </span>
              </Tooltip>
              {/* While a run is in flight this same slot becomes Cancel, so the
                  button that started the run is where you stop it. */}
              {selectedFlowStep !== 'build' &&
                (deployRunActive ? (
                  <Tooltip title="Stop the run now — the remaining waves are not merged. The deploy builds already started keep running.">
                    <span>
                      <Button
                        variant="outlined"
                        className={classes.detailsActionButton}
                        onClick={() => setCancelConfirmOpen(true)}
                      >
                        Cancel
                      </Button>
                    </span>
                  </Tooltip>
                ) : (
                  <Tooltip
                    title={
                      deployWavesReason ??
                      "Merge wave 0, then auto-advance to each next wave only after the previous wave's deploys all succeed."
                    }
                  >
                    <span>
                      <Button
                        color="primary"
                        variant="contained"
                        className={`${classes.detailsActionButton} ${classes.mergeActionButton}`}
                        disabled={deployWavesReason !== null}
                        onClick={startDeployWaves}
                      >
                        {deployVerb} in waves
                      </Button>
                    </span>
                  </Tooltip>
                ))}
            </Box>
          </Box>
        </Box>
      </Box>

      {!hasStepContent ? (
        <Typography className={classes.idle}>
          No {categoryLabel} components for {selectedModeLabel}.
        </Typography>
      ) : null}

      {selectedFlowStep === 'build' && buildRenderComponents.length > 0 && (
        <Box className={classes.phase}>
          <Box className={classes.phaseHeader}>
            <Box className={classes.phaseHeaderText}>
              <Typography variant="subtitle1" className={classes.phaseTitle}>
                Build
              </Typography>
              <Typography className={classes.phaseMeta}>
                {formatBuildPrs(countPrs(buildComponents))}
              </Typography>
            </Box>
          </Box>
          {buildRenderComponents.map(c => (
            <Box key={c.componentName} className={classes.section}>
              <ComponentMergeOpsSection
                componentPrs={c}
                onMerged={onMerged}
                onApproved={onApproved}
                compact
                expanded={isComponentDiffExpanded(c.componentName)}
                onExpandedChange={expanded =>
                  setComponentDiffExpandedValue(c.componentName, expanded)
                }
                showApprove={ownsGroup}
                showMerge={false}
                showNoPr
                selectablePrs
                isPrSelected={pr =>
                  selectedPrKeys.has(prKey(c.componentName, pr.number))
                }
                onPrSelectionChange={(pr, selected) =>
                  setPrSelected(c.componentName, pr, selected)
                }
                fetchedAtMs={fetchedAtMs}
                onPrRefreshed={onPrRefreshed}
              />
            </Box>
          ))}
        </Box>
      )}

      {selectedFlowStep !== 'build' && deployRenderSections.length > 0 && (
        <Box className={classes.phase}>
          <Box className={classes.phaseHeader}>
            <Box className={classes.phaseHeaderText}>
              <Typography variant="subtitle1" className={classes.phaseTitle}>
                Deploy
              </Typography>
              <Typography className={classes.phaseMeta}>
                {formatDeployPrs(
                  deploySections.reduce(
                    (sum, section) => sum + countPrs(section.components),
                    0,
                  ),
                )}
              </Typography>
            </Box>
          </Box>
          {deployRenderSections.map(section => (
            <Box key={section.key} className={classes.deployPhaseGroup}>
              <Box className={classes.deployPhaseRail}>
                <span className={classes.deployPhaseIndex}>{section.wave}</span>
                Wave {section.wave}
              </Box>
              <Box className={classes.deployPhaseItems}>
                {section.components.map(c => (
                  <Box
                    key={`${section.key}:${c.componentName}`}
                    className={classes.section}
                  >
                    <ComponentMergeOpsSection
                      componentPrs={c}
                      onMerged={onMerged}
                      onApproved={onApproved}
                      compact
                      expanded={isComponentDiffExpanded(c.componentName)}
                      onExpandedChange={expanded =>
                        setComponentDiffExpandedValue(c.componentName, expanded)
                      }
                      showApprove={ownsGroup}
                      showMerge={ownsGroup}
                      showNoPr
                      selectablePrs
                      isPrSelected={pr =>
                        selectedPrKeys.has(prKey(c.componentName, pr.number))
                      }
                      onPrSelectionChange={(pr, selected) =>
                        setPrSelected(c.componentName, pr, selected)
                      }
                      fetchedAtMs={fetchedAtMs}
                      onPrRefreshed={onPrRefreshed}
                    />
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Dialog
        open={previewRun !== null}
        onClose={() => setPreviewRun(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{reviewTitle(previewRun)}</DialogTitle>
        <DialogContent dividers>
          {previewRun?.operation === 'deploy' && (
            <>
              <Typography className={classes.runCopy}>
                Deploy target: {deployTargetLabel(deployTarget)}
              </Typography>
              {previewRun.executionMode === 'dry-run' ? (
                <Alert severity="info" className={classes.previewAlert}>
                  Dry run. This starts an in-page simulation for the listed
                  deploy waves; it will not merge PRs or start Cloud Deploy.
                </Alert>
              ) : (
                <Alert severity="warning" className={classes.previewAlert}>
                  Production deploy. This will merge the listed deploy PRs and
                  start Cloud Deploy for the selected target.
                </Alert>
              )}
              <Box className={classes.simulationOverview}>
                {previewDeploySections.map(section => (
                  <Chip
                    key={section.key}
                    label={`Wave ${section.wave}`}
                    color="primary"
                    size="small"
                  />
                ))}
                <Chip
                  label={executionLabel(previewRun.executionMode)}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={`Deploy target: ${deployTargetLabel(deployTarget)}`}
                  variant="outlined"
                  size="small"
                />
                <Chip
                  label={formatDeployPrs(deployPrCount)}
                  variant="outlined"
                  size="small"
                />
              </Box>
              {previewDeploySections.map((section, index) => (
                <Box key={section.key}>
                  {index > 0 && <Divider />}
                  <Box className={classes.previewPhaseCard}>
                    <Box className={classes.previewPhaseHeader}>
                      <Typography className={classes.previewPhaseTitle}>
                        Wave {section.wave}
                      </Typography>
                      <Chip
                        label={`${formatCount(
                          section.components.length,
                          'product',
                        )} · ${formatDeployPrs(countPrs(section.components))}`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                    <ProductPreviewList components={section.components} />
                    <Typography className={classes.previewWait}>
                      This wave starts after the previous wave completes.
                    </Typography>
                  </Box>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewRun(null)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={confirmRun}>
            {startButtonLabel(previewRun)}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Cancel deploy run?</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" className={classes.previewAlert}>
            The deploy builds already started are NOT cancelled — they keep
            running to completion. This only stops the run from merging any
            later wave.
          </Alert>
          <Typography className={classes.runCopy}>
            The run stops right away: the remaining waves are not merged and
            this panel stops watching. Follow the builds that are still running
            from their Cloud Build links.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirmOpen(false)}>
            Keep deploying
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={confirmCancelDeployRun}
          >
            Cancel run
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={skipTarget !== null}
        onClose={() => setSkipTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>このデプロイをスキップしますか?</DialogTitle>
        <DialogContent>
          <Typography paragraph>
            {skipTarget
              ? `${skipTarget.projectName} (${skipTarget.env}) の状態は` +
                `「${deployStateLabel(
                  skipTarget.state,
                )}」です。スキップすると ` +
                `wave ${skipTarget.wave} のゲートから外れ、この行の完了を` +
                `待たなくなります。`
              : ''}
          </Typography>
          {/* Skipping ONE failure doesn't advance the wave when others remain
              — say exactly how many are left, so "I skipped but nothing
              happened" never surprises. */}
          {skipTarget &&
            deployRun &&
            (() => {
              const others = unresolvedWaveEntries(
                deployRun.waves[deployRun.currentWaveIndex],
                envsForTarget(deployRun.target),
                deployStatuses,
                deployRun.skipped,
              ).filter(
                probe =>
                  !(
                    probe.componentName === skipTarget.componentName &&
                    probe.env === skipTarget.env
                  ),
              );
              return (
                <Typography paragraph>
                  {others.length === 0
                    ? `これをスキップすると wave ${skipTarget.wave} は完了` +
                      `扱いとなり、すぐに次の wave へ進みます。`
                    : `この wave には他に未完了が ${others.length} 件あり` +
                      `ます。すべてが成功またはスキップになった時点で次の ` +
                      `wave へ進みます。`}
                </Typography>
              );
            })()}
          <Typography paragraph>
            デプロイ自体の再実行や巻き戻しは行いません —
            失敗の対処は別途行ってください。理由は監査ログに記録されます。
          </Typography>
          <TextField
            label="理由 (必須)"
            required
            fullWidth
            multiline
            minRows={2}
            value={skipReason}
            onChange={e => setSkipReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipTarget(null)}>キャンセル</Button>
          <Button
            color="primary"
            disabled={skipReason.trim() === '' || skipSubmitting}
            onClick={confirmSkipDeploy}
          >
            スキップして続行
          </Button>
        </DialogActions>
      </Dialog>
    </Content>
  );
}
